import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { SecurityPermissionName } from '@app/core/models/permission';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  constructor(
    private authenticationService: AuthenticationService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService
  ) {}

  hasEnterpriseOptions$(options: EnterpriseOptionAuthorization): Observable<boolean> {
    if ('anyEnterpriseOption' in options) {
      return this.enterpriseAuthorizationService.hasAnyEnterpriseOption$(
        this.resolveToArray(options.anyEnterpriseOption)
      );
    } else {
      return this.enterpriseAuthorizationService.hasEnterpriseOptions$(
        this.resolveToArray(options.allEnterpriseOptions)
      );
    }
  }

  hasPermission$(permissions: PermissionAuthorization): Observable<boolean> {
    return this.authenticationService.authUser$.pipe(
      map(() => {
        if ('anyPermission' in permissions) {
          return this.authenticationService.hasAnyPermission(
            ...this.resolveToArray(permissions.anyPermission)
          );
        } else {
          return this.authenticationService.hasPermissions(
            ...this.resolveToArray(permissions.allPermissions)
          );
        }
      })
    );
  }

  hasUserLevel$(authorization: UserLevelAuthorization): Observable<boolean> {
    return this.authenticationService.authUser$.pipe(
      map((authUser) => {
        if ('requiredUserLevel' in authorization) {
          return userLevelIsEnough(authorization.requiredUserLevel, authUser);
        } else {
          return authorization.allowedUserLevels.includes(authUser.role.level);
        }
      })
    );
  }

  isAuthorized$(authorization: RequiredAuthorization): Observable<boolean> {
    let enterpriseOptions$ = of(true);
    let permissions$: Observable<boolean> = of(true);
    let userLevel$: Observable<boolean> = of(true);

    if ('anyPermission' in authorization || 'allPermissions' in authorization) {
      permissions$ = this.hasPermission$(authorization as PermissionAuthorization);
    }

    if ('anyEnterpriseOption' in authorization || 'allEnterpriseOptions' in authorization) {
      enterpriseOptions$ = this.hasEnterpriseOptions$(authorization as EnterpriseOptionAuthorization);
    }

    if ('requiredUserLevel' in authorization || 'allowedUserLevels' in authorization) {
      userLevel$ = this.hasUserLevel$(authorization as UserLevelAuthorization);
    }

    return combineLatest([enterpriseOptions$, permissions$, userLevel$]).pipe(
      map(
        ([hasEnterpriseOptions, hasPermissions, hasUserLevel]) =>
          hasEnterpriseOptions && hasPermissions && hasUserLevel
      )
    );
  }

  private resolveToArray<T>(value: T | T[]): T[] {
    return Array.isArray(value) ? value : [value];
  }
}

export type RequiredAuthorization = {
  /**
   * All specified enterprise options are required.
   */
  allEnterpriseOptions?: EnterpriseOptionType[];

  /**
   * The user must have one of the specified user levels.
   */
  allowedUserLevels?: UserLevel[];

  /**
   * All specified permissions are required.
   */
  allPermissions?: SecurityPermissionName[];

  /**
   * At least one of the specified enterprise options is required.
   */
  anyEnterpriseOption?: EnterpriseOptionType[];

  /**
   * At least one of the specified permissions is required.
   */
  anyPermission?: SecurityPermissionName[];

  /**
   * Minimum required user level.
   */
  requiredUserLevel?: UserLevel;
};

type EnterpriseOptionAuthorization =
  | { anyEnterpriseOption: EnterpriseOptionType[] }
  | { allEnterpriseOptions: EnterpriseOptionType[] };

type PermissionAuthorization =
  | { anyPermission: SecurityPermissionName[] }
  | { allPermissions: SecurityPermissionName[] };
type UserLevelAuthorization = { requiredUserLevel: UserLevel } | { allowedUserLevels: UserLevel[] };
